import { useState, useEffect, useContext, useRef } from "react";
import { View } from "../../../components/shared/Themed";
import { SafeAreaView } from "react-native-safe-area-context";
import OperationTopTabNavigator from "../../OperationUserNavigator/TabNavigator/TopTabNavigator";
import OperationBottomTabNavigator from "../../OperationUserNavigator/TabNavigator/BottomTabNavigator";
import StandardTopTabNavigator from "../../StandardUserNavigator/TabNavigation/TopTabNavigator";
import StandardBottomTabNavigator from "../../StandardUserNavigator/TabNavigation/BottomTabNavigator";
import CompanyLogo from "../../../components/core/CompanyLogo";
import Avatar from "../../../components/core/Avatar";
import * as Okta from "../../../services/oktaAuthService";
import teamConfig from "../../../utils/constants/teams";
import { userClaimInformation } from "../../../models/usersModel";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { InitUser } from "../../utils/initUser";
import { ConfigurationDispatcher } from "../../../redux/dispatcher/configurationDispatcher";
import { actionTypesConfigurations } from "../../../redux/constants/actionTypesConfigurations";
import Spinner from "../../../components/shared/Spinner";
import {
  clearSessionStorage,
  getItemFromSessionStorage,
  setItemInSessionStorage,
} from "../../../services/utils/storageService";
import io from "socket.io-client";
import mapiConfig from "../../../configs/mapi";
import { useToast } from "react-native-toast-notifications";
import { Audio } from "expo-av";
import { receivedNotification } from "../../../redux/reducers/notificationReducer";
import { getAllNotificationsCount } from "../../../redux/dispatcher/notificationDispatcher";

export default function RootTabNavigator({ navigation }: any) {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [userProfile, setUserProfile] = useState<userClaimInformation>();
  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const { isUserAuthorized, currentCompany, userInfo } = useSelector(
    (state: any) => state.configurations
  );
  const { notificationCount } = useSelector((state: any) => state.notification);
  const storedAlertFlag = getItemFromSessionStorage("login-alert-shown");
  const [alertShown, setAlertShown] = useState<boolean>(
    storedAlertFlag === "true"
  );

  const setLogo = (data: string) => {
    ConfigurationDispatcher(actionTypesConfigurations.Logo, data);
  };

  const setIsUserAuthorized = (data: boolean) => {
    ConfigurationDispatcher(actionTypesConfigurations.IsUserAuthorized, data);
  };

  useEffect(() => {
    setUserProfile(userInfo);
  }, [userInfo]);

  useEffect(() => {
    ConfigurationDispatcher(actionTypesConfigurations.Navigation, navigation);
  }, []);

  // Sound files
  const NOTIFICATION_SOUNDS = {
    default: require("../../../assets/sounds/notification-default.wav"),
  };

  const SOCKET_SERVER_URL = mapiConfig.api.domain;
  const [socketConnected, setSocketConnected] = useState<boolean>(false);
  const socketRef = useRef<any>(null);
  const soundRef = useRef<Audio.Sound | null>(null);
  const toast = useToast();
  const dispatch = useDispatch<any>();

  // Function to play notification sounds
  const playNotificationSound = async (type = "default") => {
    try {
      // Unload any previous sound
      if (soundRef.current) {
        await soundRef.current.unloadAsync();
      }

      // Select the sound based on notification type
      const soundToPlay =
        NOTIFICATION_SOUNDS[type] || NOTIFICATION_SOUNDS.default;

      // Load and play the sound
      const { sound } = await Audio.Sound.createAsync(soundToPlay);
      soundRef.current = sound;

      await sound.playAsync();
    } catch (error) {
      console.error("Error playing notification sound:", error);
    }
  };

  const showNotificationToast = (data: any) => {
    // Default to 'info' if no type provided
    const notificationType = data.type || "info";

    // Play appropriate notification sound
    playNotificationSound(notificationType);

    // Show the toast using react-native-toast-notifications
    toast.show(data.message, {
      type: notificationType,
      duration: 4000,
      placement: "top",
      onPress: () => {
        // Navigate to notifications screen if applicable
        if (data.navigateTo) {
          navigation.navigate("UserSettings", {
            screen: "Notification",
            params: {
              screen: "UserSettingNotificationScreen",
            },
          });
        }
      },
    });
  };

  // Audio setup
  useEffect(() => {
    // Initialize audio
    const initAudio = async () => {
      try {
        await Audio.setAudioModeAsync({
          playsInSilentModeIOS: true,
          staysActiveInBackground: false,
          shouldDuckAndroid: true,
        });
      } catch (error) {
        console.error("Failed to initialize audio:", error);
      }
    };

    initAudio();

    // Cleanup audio on unmount
    return () => {
      if (soundRef.current) {
        soundRef.current.unloadAsync();
      }
    };
  }, []);
  // Socket connection establishment
  useEffect(() => {
    // Connect to socket when user is authorized and we have user info
    if (isUserAuthorized && userInfo && userInfo.user_id) {
      // Create socket connection if it doesn't exist
      if (!socketRef.current) {
        console.log("Establishing socket connection...");
        socketRef.current = io(SOCKET_SERVER_URL, {
          transports: ["websocket"],
        });

        // Socket connection event handlers
        socketRef.current.on("connect", () => {
          console.log(`Socket connected:`, socketRef.current.id);
          setSocketConnected(true);

          // Register the user with their ID
          socketRef.current.emit("register", userInfo.login);
        });

        socketRef.current.on("registered", (data: any) => {
          console.log("User registered with socket:", data);
        });

        // Handle incoming notifications
        socketRef.current.on("new_notification", (data: any) => {
          console.log("New notification received:", data);
          showNotificationToast(data);
          dispatch(receivedNotification());
        });

        socketRef.current.on("disconnect", () => {
          console.log("Socket disconnected");
          setSocketConnected(false);
        });

        socketRef.current.on("error", (error: any) => {
          console.error("Socket error:", error);
        });
      }
    }

    // Cleanup function to disconnect socket when component unmounts
    return () => {
      if (socketRef.current) {
        console.log("Disconnecting socket");
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocketConnected(false);
      }
    };
  }, [isUserAuthorized, userInfo]);

  useEffect(() => {
    let isMounted = true;

    if (!isUserAuthorized) {
      Okta.getSession().then((oktaData) => {
        if (oktaData === null) {
          // fallback to bypass custom authentication
          Okta.getCustomSession().then((customData: any) => {
            if (customData === null) {
              clearSessionStorage();
              navigation.navigate("Login", { screen: "login" });
            } else {
              setIsUserAuthorized(true);

              // Get user details from mongodb, Okta is only using for authenication
              InitUser(isMounted, setLoading, setLogo);

              const inviteContext = getItemFromSessionStorage("invite-context");
              if (inviteContext) {
                window.location.href = inviteContext;
              }
            }
          });
        } else {
          setIsUserAuthorized(true);

          // Get user details from mongodb, Okta is only using for authenication
          InitUser(isMounted, setLoading, setLogo);

          const inviteContext = getItemFromSessionStorage("invite-context");
          if (inviteContext) {
            window.location.href = inviteContext;
          }
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [currentCompany]);

  useEffect(() => {
    if (isUserAuthorized && !alertShown) {
      dispatch(getAllNotificationsCount());
    }
  }, [isUserAuthorized, alertShown, dispatch]);

  useEffect(() => {
    if (isUserAuthorized && notificationCount > 0 && !alertShown) {
      const data = {
        message: `You have ${notificationCount} unread notifications`,
        notificationType: "NOTIFICATION_ALERT",
        navigateTo: "UserSettingNotificationScreen",
      };
      showNotificationToast(data);

      setAlertShown(true);
      setItemInSessionStorage("login-alert-shown", "true");
    }
  }, [notificationCount, isUserAuthorized, alertShown]);

  const {
    sales,
    operation,
    manager,
    admin,
    warehouse,
    production,
    purchasing,
    china,
    salesManager,
  } = teamConfig.teams;
  const teams = [operation, manager, admin, warehouse];
  const limitedTeam = [sales, salesManager, production, purchasing, china];

  return (
    <SafeAreaView style={{ height: "100%", width: "100%" }}>
      <View
        style={{
          height: "100%",
          width: "100%",
          backgroundColor: "white",
          flexDirection: "row",
        }}
      >
        {isUserAuthorized && !isLoading ? (
          <View style={{ height: "100%", width: "100%" }}>
            {!isSmallDevice && <CompanyLogo />}
            {isSmallDevice &&
              limitedTeam.includes(userProfile?.team.toUpperCase()) && (
                <StandardBottomTabNavigator />
              )}
            {isSmallDevice &&
              teams.includes(userProfile?.team.toUpperCase()) && (
                <OperationBottomTabNavigator />
              )}
            {!isSmallDevice &&
              limitedTeam.includes(userProfile?.team.toUpperCase()) && (
                <StandardTopTabNavigator />
              )}
            {!isSmallDevice &&
              teams.includes(userProfile?.team.toUpperCase()) && (
                <OperationTopTabNavigator />
              )}
            {!isSmallDevice && <Avatar />}
          </View>
        ) : (
          <View
            style={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Spinner
              size="small"
              color="#74BBFF"
              styles={{
                flexDirection: "row",
                justifySelf: "center",
                alignSelf: "center",
              }}
            />
          </View>
        )}
      </View>
    </SafeAreaView>
  );
}
